import { getBackend } from "./api.ts";

/**
 * Fire-and-forget analytics beacon for the embedded widget.
 *
 * Design:
 *   - One function, `track(event, props)`. Never throws, never blocks.
 *     Telemetry failures must never affect UX.
 *   - Uses `fetch(..., { keepalive: true })` so events still ship when
 *     the page is navigating away (e.g., shopper clicks "View bag" right
 *     after add_to_cart_click).
 *   - Sends to the same Railway origin that serves embed.js — same CORS
 *     allowlist as /chat. No new infra.
 *   - No PII. Message contents are NEVER sent — those live in the
 *     conversations table for QA, and analytics is for funnel shape, not
 *     content review.
 *
 * Trade-offs:
 *   - We do NOT batch events. The volume per session is small (<50 events
 *     per chat session in the worst case) and 50 keepalive fetches is
 *     fine for any browser. Adding batching adds reliability risk
 *     (events lost on navigation) without saving meaningful bytes.
 *   - We don't read the response. The server returns 204 anyway, and the
 *     widget couldn't do anything useful with the result.
 *   - When `navigator.sendBeacon` is available, prefer it for `pagehide`-
 *     style events because it's guaranteed to fire after the page is
 *     gone. We don't have any such events today, but keep the option
 *     open in case we add one (e.g., dwell_time on close).
 */

export type AnalyticsEvent =
  | "bubble_view"
  | "modal_open"
  | "modal_close"
  | "chip_click"
  | "inline_chip_click"
  | "inline_send_click"
  | "pdp_inline_view"
  | "message_send"
  | "assistant_done"
  | "product_card_view"
  | "product_card_click"
  | "add_to_cart_click"
  | "add_to_cart_success"
  | "add_to_cart_error"
  | "error";

export type AnalyticsSource =
  | "floating-bubble"      // shopper clicked the corner bubble
  | "inline-pdp-chip"      // shopper clicked a chip in the inline PDP block
  | "inline-pdp-send"      // shopper hit send on the inline PDP block
  | "nav-link"             // AI Beauty nav link → #ai-chat
  | "hash-url"             // direct URL with #ai-chat or ?chat=open
  | "suggested-chip"       // chip in the modal's greeting state
  | "modal-composer"       // direct typing into modal composer
  | "card-link"            // product card link click
  | "card-add-btn";        // product card "Add to bag" click

interface TrackArgs {
  source?: AnalyticsSource;
  productHandle?: string;
  properties?: Record<string, unknown>;
}

// Session token is set by api.ts after the first /session fetch. We
// attach it to every event so funnels can be aggregated per visitor.
// Stored here as a module-level singleton because every analytics call
// is from the same widget instance; passing it explicitly would clutter
// every call site.
let _sessionToken: string | null = null;
export function setAnalyticsSession(token: string | null): void {
  _sessionToken = token;
}

/**
 * Fire an analytics event. Never throws. Returns immediately.
 *
 * `event` is the typed event name. `args.source` describes WHERE on the
 * UI the event came from (floating bubble vs inline PDP chip etc.).
 * `args.properties` is arbitrary small JSON for surface-specific context
 * (chip text, error reason, message length bucket, etc.).
 */
export function track(event: AnalyticsEvent, args: TrackArgs = {}): void {
  if (typeof window === "undefined") return; // SSR guard

  try {
    const payload = JSON.stringify({
      event_type: event,
      session_token: _sessionToken,
      source: args.source,
      product_handle: args.productHandle,
      properties: args.properties ?? {},
      page_url: window.location.href.slice(0, 500),
    });

    // sendBeacon is ideal for "fire before unload" but doesn't allow
    // setting Content-Type to application/json on most browsers (it
    // sends as text/plain). The server accepts JSON either way (it
    // does a JSON.parse), but to keep things consistent we use fetch
    // with keepalive — which DOES preserve Content-Type and survives
    // page navigation.
    void fetch(`${getBackend()}/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      // Don't send cookies / credentials. The analytics ingest is anonymous
      // and we want to avoid any per-visitor session weirdness.
      credentials: "omit",
    }).catch(() => {
      // Swallow. Analytics failures are never user-facing.
    });
  } catch {
    // JSON.stringify or fetch construction can't realistically throw,
    // but defensive: never let a telemetry path crash the widget.
  }
}
